import { zodResolver } from "@hookform/resolvers/zod";
import { BedIcon, PlusCircleIcon, TrashIcon } from "@phosphor-icons/react";
import { times } from "lodash-es";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useIntl } from "react-intl";
import { object } from "zod";

import { useFeatureIsOn } from "@/app/featureFlags";
import { Button } from "@/components/Button/Button";
import { Card } from "@/components/Card/Card";
import { CounterButton } from "@/components/CounterButton/CounterButton";
import { IconButton } from "@/components/IconButton/IconButton";
import { Select, SelectItem } from "@/components/Select";
import { TravellerTypeSelectionItem } from "@/components/TravellerTypeSelectionItem/TravellerTypeSelectionItem";
import type { TRoom } from "@/features/hotels/types/types";
import {
  MAX_CHILDREN_PER_ROOM,
  MAX_TOTAL_VISITORS,
  MAX_VISITORS_PER_ROOM,
} from "@/features/hotels/utils/constants";
import {
  getRoomsValidationSchema,
  type HotelSearchFormValues,
} from "@/features/hotels/utils/hotelsValidation";
import { getTotalVisitors } from "@/features/hotels/utils/rooms";
import { MIN_ADULT_AGE } from "@/utils/constants";

const newRoom: TRoom = {
  adults: 1,
  children: 0,
  childrenAges: [],
};

type Props = {
  value: HotelSearchFormValues["rooms"];
  onConfirm: (values: HotelSearchFormValues["rooms"]) => void;
};

export function VisitorsSelection({ value, onConfirm }: Props) {
  const intl = useIntl();

  const methods = useForm<Pick<HotelSearchFormValues, "rooms">>({
    defaultValues: {
      rooms: value,
    },
    resolver: zodResolver(
      object({
        rooms: getRoomsValidationSchema(intl),
      }),
    ),
  });

  const { handleSubmit, control } = methods;

  const {
    fields: rooms,
    remove,
    update,
    append,
  } = useFieldArray({
    control,
    name: "rooms",
  });

  const isMultiRoomBookingEnabled = useFeatureIsOn("multi-room-booking");

  const totalVisitors = getTotalVisitors(rooms);
  const remainingCapacity = MAX_TOTAL_VISITORS - totalVisitors;

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={async (event) => {
          event.stopPropagation();

          await handleSubmit((data) => {
            onConfirm(data.rooms);
          })(event);

          // scroll to first error, if any
          event.target
            .querySelector("[aria-invalid='true']")
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        }}
        className="flex w-full basis-full flex-col"
      >
        <div className="mb-3 text-sm text-text-primary">
          {intl.formatMessage(
            {
              defaultMessage:
                "A maximum of {maxVisitors} guests is allowed across all rooms.",
              description:
                "Info notice showing maximum total guests allowed across all rooms",
            },
            { maxVisitors: MAX_TOTAL_VISITORS },
          )}
        </div>

        <div className="space-y-3">
          {rooms.map((room, roomIndex) => {
            const currentChildCount = room.childrenAges?.length ?? 0;
            const maxAdultCount = Math.min(
              MAX_VISITORS_PER_ROOM - currentChildCount,
              (room.adults ?? 1) + remainingCapacity,
            );
            const maxChildCount = Math.min(
              MAX_VISITORS_PER_ROOM - (room.adults ?? 1),
              MAX_CHILDREN_PER_ROOM,
              currentChildCount + remainingCapacity,
            );

            return (
              <Card
                key={room.id}
                icon={
                  <BedIcon
                    size={24}
                    color="var(--color-surface-pressed-accent-strong)"
                  />
                }
                title={
                  isMultiRoomBookingEnabled
                    ? `${intl.formatMessage({ defaultMessage: "Room", description: "Generic label for a single room in the room configuration list" })} ${roomIndex + 1}`
                    : intl.formatMessage({
                        defaultMessage: "Room",
                        description:
                          "Generic label for a single room in the room configuration list",
                      })
                }
                action={
                  rooms.length > 1 ? (
                    <IconButton
                      aria-label={intl.formatMessage({
                        defaultMessage: "Remove",
                        description:
                          "Generic 'Remove' button label to delete or deselect an item",
                      })}
                      color="error"
                      onClick={() => remove(roomIndex)}
                      disablePadding
                    >
                      <TrashIcon size={24} />
                    </IconButton>
                  ) : null
                }
              >
                <div className="-my-3 divide-y divide-stroke-neutral-soft *:py-3">
                  <TravellerTypeSelectionItem
                    label={intl.formatMessage({
                      defaultMessage: "Adult",
                      description: "Guest type label: adult",
                    })}
                    description={intl.formatMessage({
                      defaultMessage: "12+ years old",
                      description: "Age range qualifier: 12 years and over",
                    })}
                  >
                    <CounterButton
                      value={room.adults ?? 1}
                      onChange={(value) =>
                        update(roomIndex, {
                          ...room,
                          adults: value,
                        })
                      }
                      minValue={1}
                      maxValue={maxAdultCount}
                      minusButtonAriaLabel={intl.formatMessage({
                        defaultMessage: "Remove adult",
                        description:
                          "Accessible label for the button that removes an adult guest from a room",
                      })}
                      plusButtonAriaLabel={intl.formatMessage({
                        defaultMessage: "Add adult",
                        description:
                          "Accessible label for the button that adds an adult guest to a room",
                      })}
                    />
                  </TravellerTypeSelectionItem>

                  <div>
                    <TravellerTypeSelectionItem
                      label={intl.formatMessage({
                        defaultMessage: "Child",
                        description: "Guest type label: child",
                      })}
                      description={intl.formatMessage({
                        defaultMessage: "Under 12 years old",
                        description:
                          "Age range qualifier for child hotel guests: under 12 years old",
                      })}
                    >
                      <CounterButton
                        value={currentChildCount}
                        onChange={(value) => {
                          const nextAges = Array.from(
                            { length: value },
                            (_, i) => room.childrenAges?.[i],
                          );
                          update(roomIndex, {
                            ...room,
                            children: value,
                            childrenAges: nextAges,
                          });
                        }}
                        maxValue={maxChildCount}
                        minusButtonAriaLabel={intl.formatMessage({
                          defaultMessage: "Remove child",
                          description:
                            "Accessible label for the button that removes a child guest from a room",
                        })}
                        plusButtonAriaLabel={intl.formatMessage({
                          defaultMessage: "Add child",
                          description:
                            "Accessible label for the button that adds a child guest to a room",
                        })}
                      />
                    </TravellerTypeSelectionItem>

                    {/* Children ages */}
                    <div className="grid grid-cols-2 gap-2 not-empty:mt-4">
                      {times(currentChildCount, (childIndex) => {
                        return (
                          <Controller
                            key={childIndex}
                            control={control}
                            name={`rooms.${roomIndex}.childrenAges.${childIndex}`}
                            render={({ field, fieldState }) => (
                              <Select
                                label={intl.formatMessage(
                                  {
                                    defaultMessage: "Child {number} age",
                                    description:
                                      "Age selector label for a child guest (e.g. 'Child 2 age')",
                                  },
                                  {
                                    number: childIndex + 1,
                                  },
                                )}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  field.onChange(Number(e.target.value));
                                }}
                              >
                                {times(MIN_ADULT_AGE, (i) => (
                                  <SelectItem key={i} value={i}>
                                    {i === 0
                                      ? intl.formatMessage({
                                          defaultMessage: "Under 1",
                                          description:
                                            "hotels.search.underOneYearOld",
                                        })
                                      : i}
                                  </SelectItem>
                                ))}
                              </Select>
                            )}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {isMultiRoomBookingEnabled ? (
          <Button
            type="button"
            variant="text"
            startIcon={<PlusCircleIcon size={24} />}
            disabled={remainingCapacity <= 0}
            onClick={(e) => {
              append(newRoom);
              e.currentTarget.blur();
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Add another room",
              description: "Button to add another room to the hotel search",
            })}
          </Button>
        ) : null}
        <div className="mt-auto">
          <Button fullWidth type="submit" variant="contained">
            {intl.formatMessage({
              defaultMessage: "Confirm",
              description:
                "Generic 'Confirm' button label to submit or accept an action",
            })}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
